import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, QrCode, RefreshCw, RotateCcw, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PaneHeader } from '@/components/settings/pane-header';
import { fetchWhatsappGateways, relinkWhatsappGateway } from '@/lib/api/whatsapp-connections';
import { cn } from '@/lib/utils';

// Poll cadence: slow while just watching the list, fast while a QR dialog is open (the QR rotates
// and we want to catch the flip to connected quickly).
const LIST_POLL_MS = 10000;
const QR_POLL_MS = 2500;

// Effective status → pill styling + copy. Merges Layer B (backend WS) and Layer A (WhatsApp login):
// "offline" means the gateway process is unreachable; the rest are the gateway's self-report.
const STATUS_META = {
  connected: { label: 'Connected', dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', pulse: false },
  qr: { label: 'Awaiting scan', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', pulse: true },
  connecting: { label: 'Connecting…', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', pulse: true },
  logged_out: { label: 'Logged out', dot: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400', pulse: false },
  offline: { label: 'Process offline', dot: 'bg-slate-400', text: 'text-muted-foreground', pulse: false },
  unknown: { label: 'Unknown', dot: 'bg-slate-400', text: 'text-muted-foreground', pulse: false },
};

function statusMeta(status) {
  return STATUS_META[status] || STATUS_META.unknown;
}

function StatusPill({ status }) {
  const meta = statusMeta(status);
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11.5px] font-bold', meta.text)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot, meta.pulse && 'animate-pulse')} />
      {meta.label}
    </span>
  );
}

// `canEdit` is integrations:manage. Without it the row is a pure status readout: no QR (the
// backend blanks qrDataUrl below manage anyway — this just stops offering a dead button) and
// no re-bind.
function GatewayRow({ gateway, busy, canEdit, onShowQr, onRelink }) {
  const connected = gateway.status === 'connected';
  return (
    <div className="flex items-center gap-4 rounded-xl bg-card p-4 shadow-[inset_0_0_0_1px_var(--border)]">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
        <Smartphone className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5">
          <span className="truncate text-base font-extrabold text-foreground">{gateway.label || gateway.botId}</span>
          <StatusPill status={gateway.status} />
        </div>
        <p className="mt-0.5 truncate font-mono text-[12px] text-muted-foreground">{gateway.botId}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {!gateway.backendConnected ? (
          <span className="text-[12px] font-semibold text-muted-foreground">Gateway process offline</span>
        ) : !canEdit ? (
          <span className="text-[12px] font-semibold text-muted-foreground">View only</span>
        ) : (
          <>
            {!connected ? (
              <Button type="button" variant="outline" size="sm" onClick={() => onShowQr(gateway.botId)}>
                <QrCode className="mr-1.5 h-4 w-4" aria-hidden />
                Show QR
              </Button>
            ) : null}
            <Button
              type="button"
              variant={connected ? 'outline' : 'default'}
              size="sm"
              disabled={busy}
              onClick={() => onRelink(gateway)}
            >
              {busy ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden />
              )}
              Re-bind
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// The QR / re-bind dialog. Reads the live gateway (by botId) from the polled list so it updates in
// place as the QR rotates and when the scan lands (status → connected).
function QrDialog({ gateway, busy, onRelink, onClose }) {
  const open = Boolean(gateway);
  const status = gateway?.status;
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link {gateway?.label || gateway?.botId || 'number'}</DialogTitle>
          <DialogDescription>
            On the phone: WhatsApp → Settings → Linked devices → Link a device, then scan.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          {status === 'connected' ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                <Smartphone className="h-7 w-7" aria-hidden />
              </span>
              <p className="text-sm font-semibold text-foreground">This number is linked.</p>
              <p className="text-[13px] text-muted-foreground">You can close this dialog.</p>
            </div>
          ) : gateway?.qrDataUrl ? (
            <>
              <div className="rounded-lg bg-white p-3">
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <img src={gateway.qrDataUrl} alt="WhatsApp linking QR code" className="h-60 w-60" />
              </div>
              <StatusPill status={status} />
            </>
          ) : status === 'connecting' ? (
            <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
              <p className="text-[13px]">Connecting…</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-[13px] text-muted-foreground">
                No active QR. Re-bind to wipe the old session and generate a fresh code.
              </p>
              <StatusPill status={status} />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button type="button" size="sm" disabled={busy} onClick={() => onRelink(gateway)}>
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> : <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden />}
            Re-bind
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// canEdit = integrations:manage (integrations:view alone is a read-only status board).
// Defaults false so any future caller that forgets the prop fails closed.
export function WhatsappConnectionsSubPage({ canEdit = false }) {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrBotId, setQrBotId] = useState(null);
  const [busyBotId, setBusyBotId] = useState(null);

  const load = useCallback(async () => {
    try {
      const rows = await fetchWhatsappGateways();
      setGateways(rows);
    } catch {
      // Keep the last known list; a transient failure must not blank the panel.
    } finally {
      setLoading(false);
    }
  }, []);

  // Self-scheduling poll: re-reads immediately then waits, at a cadence that speeds up while the QR
  // dialog is open. Re-runs when qrBotId flips (open/close) to switch cadence.
  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = async () => {
      if (cancelled) return;
      await load();
      if (cancelled) return;
      timer = setTimeout(tick, qrBotId ? QR_POLL_MS : LIST_POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [load, qrBotId]);

  const handleRelink = useCallback(
    async (gateway) => {
      // Defence in depth — the buttons are hidden without manage, and the server enforces too.
      if (!canEdit) return;
      if (!gateway?.botId) return;
      if (gateway.status === 'connected') {
        const ok = window.confirm(
          `Re-bind ${gateway.label || gateway.botId}? This logs out the currently linked phone and shows a new QR to scan.`,
        );
        if (!ok) return;
      }
      setBusyBotId(gateway.botId);
      try {
        await relinkWhatsappGateway({ botId: gateway.botId });
        toast.success('Re-bind requested — a fresh QR will appear shortly.');
        setQrBotId(gateway.botId);
        load();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not request re-bind');
      } finally {
        setBusyBotId(null);
      }
    },
    [load, canEdit],
  );

  const activeGateway = useMemo(
    () => gateways.find((g) => g.botId === qrBotId) || null,
    [gateways, qrBotId],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        title="WhatsApp Connections"
        description={
          canEdit
            ? 'Live connection status for each configured WhatsApp number. Show a QR to re-bind a phone when a number is disconnected, or re-bind on demand.'
            : 'Live connection status for each configured WhatsApp number. Showing the QR code and re-binding a number require Integrations → Manage.'
        }
        action={
          <Button type="button" variant="outline" size="sm" onClick={load}>
            <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden />
            Refresh
          </Button>
        }
      />
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto flex max-w-4xl flex-col gap-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading connections…
            </div>
          ) : gateways.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-3 py-10 text-center text-sm text-muted-foreground">
              No WhatsApp numbers are configured on the backend.
            </div>
          ) : (
            gateways.map((gateway) => (
              <GatewayRow
                key={gateway.botId}
                gateway={gateway}
                busy={busyBotId === gateway.botId}
                canEdit={canEdit}
                onShowQr={setQrBotId}
                onRelink={handleRelink}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Without manage the dialog can never mount: activeGateway resolves from qrBotId, which
          only the (hidden) Show QR button and a (blocked) re-bind ever set. */}
      <QrDialog
        gateway={canEdit ? activeGateway : null}
        busy={busyBotId === (activeGateway?.botId || '')}
        onRelink={handleRelink}
        onClose={() => setQrBotId(null)}
      />
    </div>
  );
}
